import { useEffect, useMemo, useRef } from 'react'
import { getActionErrorPayload } from '~/utils/actionErrorPayload'
import { emitApiError } from '~/utils/apiErrorEvents'

import { useActionData, useFetchers, useNavigation } from '@remix-run/react'

function collectErrorPayloads(...sources: unknown[]): ReturnType<typeof getActionErrorPayload>[] {
  return sources
    .map((source) => getActionErrorPayload(source))
    .filter((payload): payload is NonNullable<typeof payload> => payload !== null)
}

function getRemixActionDataValues(): unknown[] {
  if (typeof window === 'undefined') {
    return []
  }

  const routerState = (window as any).__remixRouter?.state
  const contextState = (window as any).__remixContext?.state
  const actionDataMap = routerState?.actionData ?? contextState?.actionData

  if (!actionDataMap || typeof actionDataMap !== 'object') {
    return []
  }

  return Object.values(actionDataMap)
}

/**
 * Surfaces Remix action / fetcher errors (e.g. fetchWrapper failures) as a toast via ApiErrorToast.
 */
export function GlobalActionErrorHandler() {
  const actionData = useActionData()
  const fetchers = useFetchers()
  const navigation = useNavigation()
  const lastToastRef = useRef<string | null>(null)

  const errorPayload = useMemo(() => {
    const fromAction = collectErrorPayloads(actionData)
    if (fromAction.length) return fromAction[0]

    for (const fetcher of fetchers) {
      if (fetcher.state !== 'idle' || !fetcher.data) continue
      const fromFetcher = collectErrorPayloads(fetcher.data)
      if (fromFetcher.length) return fromFetcher[0]
    }

    for (const remixActionData of getRemixActionDataValues()) {
      const fromRemix = collectErrorPayloads(remixActionData)
      if (fromRemix.length) return fromRemix[0]
    }

    return null
  }, [actionData, fetchers, navigation.state])

  useEffect(() => {
    if (navigation.state !== 'idle') {
      return
    }

    const message = errorPayload?.formError || errorPayload?.error
    if (!message || message === lastToastRef.current) {
      return
    }

    lastToastRef.current = message
    emitApiError(message)
  }, [errorPayload, navigation.state])

  return null
}
